/**
 * CaricaProbabiliPage — import probabili formazioni (Andrea 2026-05-19).
 *
 * Workflow no-scraping (lecito, uso personale):
 *   1. Admin apre Sky Sport / Fantacalcio.it / Gazzetta sui siti di
 *      "probabili formazioni" della giornata.
 *   2. Copia il blocco testo (Cmd/Ctrl+A → Cmd/Ctrl+C).
 *   3. Incolla qui sotto, seleziona giornata + fonte, click Importa.
 *   4. Parser fuzzy-match → upsert in DB.
 *   5. Schiera Formazione mostra il badge % accanto al nome giocatore.
 */

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { useStagioneAttiva } from "../../hooks/useStagione";
import { api } from "../../lib/api";
import { GiornataSelector } from "../common/GiornataSelector";
import { badgeProbabilita } from "../common/probabilita";
import { MobileShell } from "../shell/MobileShell";

interface ImportProbabiliResponse {
  parsate: number;
  abbinate: number;
  non_abbinate: string[];
  upsert_eseguiti: number;
}

interface AnteprimaRow {
  nome_input: string;
  nome_db: string;
  probabilita: number;
  note: string | null;
  score: number;
}

interface AnteprimaResponse {
  matched: AnteprimaRow[];
  non_abbinati: string[];
}

const FONTI = [
  { value: "Sky", label: "Sky Sport" },
  { value: "Fantacalcio.it", label: "Fantacalcio.it" },
  { value: "Gazzetta", label: "Gazzetta dello Sport" },
  { value: "FantaMaster", label: "FantaMaster" },
  { value: "manuale", label: "Manuale / altro" },
];

export function CaricaProbabiliPage() {
  useAuth();
  const { data: stagioneAttiva } = useStagioneAttiva();
  const stagioneId = stagioneAttiva?.id ?? null;
  const [giornataId, setGiornataId] = useState("");
  const [fonte, setFonte] = useState("Sky");
  const [paste, setPaste] = useState("");
  const [report, setReport] = useState<ImportProbabiliResponse | null>(null);
  const [anteprima, setAnteprima] = useState<AnteprimaResponse | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  const importMut = useMutation({
    mutationFn: async () => {
      const res = await api.post<ImportProbabiliResponse>(
        `/admin/giornate/${giornataId}/probabili`,
        { paste, fonte },
      );
      return res.data;
    },
    onSuccess: (data) => {
      setReport(data);
      setAnteprima(null);
      setErrore(null);
    },
    onError: (err) => {
      const detail = (err as { response?: { data?: { detail?: string } }; message?: string })
        .response?.data?.detail;
      setErrore(detail ?? (err as Error).message ?? "errore");
    },
  });

  // Scraping on-demand (stesso motore del job automatico): fonte a
  // scelta + verifica che la pagina dichiari la giornata giusta.
  const [fonteScrape, setFonteScrape] = useState<"fantacalcio" | "sky">("fantacalcio");
  const [mismatchMsg, setMismatchMsg] = useState<string | null>(null);
  const scrapeMut = useMutation({
    mutationFn: async (forza: boolean) => {
      const res = await api.post<ImportProbabiliResponse & { giornata_pagina: number | null }>(
        `/admin/giornate/${giornataId}/probabili/scrape`,
        { fonte: fonteScrape, forza },
        { timeout: 60000 }, // fetch esterno può richiedere qualche secondo
      );
      return res.data;
    },
    onSuccess: (data) => {
      setReport(data);
      setAnteprima(null);
      setErrore(null);
      setMismatchMsg(null);
    },
    onError: (err, forza) => {
      const e = err as {
        response?: { status?: number; data?: { detail?: string } };
        message?: string;
      };
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && !forza) {
        // Giornata della pagina ≠ giornata attesa: chiedi conferma
        setMismatchMsg(detail ?? "La pagina mostra una giornata diversa.");
        setErrore(null);
        return;
      }
      setErrore(detail ?? (err as Error).message ?? "errore");
      setMismatchMsg(null);
    },
  });

  const anteprimaMut = useMutation({
    mutationFn: async () => {
      const res = await api.post<AnteprimaResponse>(
        `/admin/giornate/${giornataId}/probabili/anteprima`,
        { paste, fonte },
      );
      return res.data;
    },
    onSuccess: (data) => {
      setAnteprima(data);
      setReport(null);
      setErrore(null);
    },
    onError: (err) => {
      const detail = (err as { response?: { data?: { detail?: string } }; message?: string })
        .response?.data?.detail;
      setErrore(detail ?? (err as Error).message ?? "errore");
    },
  });

  return (
    <MobileShell logoSuffix="PROBABILI">
      <h2 className="font-display text-3xl text-accent tracking-wide">Probabili formazioni</h2>
      <p className="text-xs text-fg-muted">
        Aggiornamento automatico due volte a turno (Sky la mattina dopo l'ultima partita,
        Fantacalcio.it a mezzogiorno della vigilia) — qui puoi lanciarlo a mano o incollare il testo
        come sempre. I badge % compaiono in Schiera Formazione.
      </p>

      <details className="card text-xs">
        <summary className="cursor-pointer font-bold text-accent">
          💡 Come copiare da Fantacalcio.it / Sky / Gazzetta
        </summary>
        <div className="mt-2 space-y-2 text-fg-muted">
          <div>
            <b className="text-fg">Fantacalcio.it (layout 2 colonne):</b>
            <ol className="list-decimal ml-5 mt-1 space-y-0.5">
              <li>Vai sulla pagina della partita (es. Fiorentina-Atalanta).</li>
              <li>
                Sotto al campo c'è la lista giocatori con barre %. Seleziona dal nome della prima
                squadra fino all'ultimo giocatore della seconda (Ctrl+A funziona se sei in zoom
                limitato).
              </li>
              <li>Copia (Ctrl+C) e incolla qui sotto.</li>
              <li>
                Il parser gestisce il layout a 2 colonne ("De Gea 80% 70% Carnesecchi" → 2 entry
                separate) e ignora le pill ruolo P/D/C/A.
              </li>
            </ol>
          </div>
          <div>
            <b className="text-fg">Sky Sport / Gazzetta:</b>
            <ol className="list-decimal ml-5 mt-1 space-y-0.5">
              <li>
                Copia il blocco dei probabili schieramenti — formati tipo "Cognome 90%" / "Cognome
                (titolare)" / "Cognome (squalificato)" sono tutti riconosciuti.
              </li>
              <li>
                Puoi incollare anche più squadre alla volta (tutta la giornata): le righe vuote /
                intestazioni vengono saltate.
              </li>
            </ol>
          </div>
          <div className="text-fg-muted">
            ⚠ Se trovi nomi <b>non abbinati</b> nel report, sono refusi del sito o calciatori non in
            Serie A (es. nomi corti tipo "Vlasic" mentre il DB ha "Nikola Vlasic"). Per i casi
            ricorrenti puoi aggiungere alias da <code>/admin/giocatori</code> o ignorare.
          </div>
        </div>
      </details>

      <div className="card space-y-3">
        <div>
          <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">
            Giornata
          </label>
          <GiornataSelector
            stagioneId={stagioneId}
            value={giornataId}
            onChange={setGiornataId}
            autoSelectFirstAperta
          />
        </div>

        {/* Scraping on-demand a scelta fonte — stesso motore del job
            automatico (Sky la mattina dopo il turno, Fantacalcio la
            vigilia). Verifica che la pagina dichiari la giornata giusta:
            se non corrisponde chiede conferma prima di importare. */}
        <div className="border border-success/40 bg-success/5 rounded-card p-3 space-y-2">
          <div className="text-sm font-bold text-success">🚀 Modo rapido: scarica dal sito</div>
          <p className="text-xs text-fg-muted">
            Un click → il backend legge la pagina ufficiale della fonte e importa tutto (Fantacalcio
            con le %, Sky con titolari/dubbi/out). Gira anche da solo: Sky la mattina dopo il turno,
            Fantacalcio a mezzogiorno della vigilia — qui lo lanci quando vuoi tu.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={fonteScrape}
              onChange={(e) => setFonteScrape(e.target.value as "fantacalcio" | "sky")}
              className="px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm"
              aria-label="Fonte scraping"
            >
              <option value="fantacalcio">Fantacalcio.it (con %)</option>
              <option value="sky">Sky Sport (titolari/out)</option>
            </select>
            <button
              type="button"
              onClick={() => scrapeMut.mutate(false)}
              disabled={!giornataId.trim() || scrapeMut.isPending}
              className="btn-primary text-sm disabled:opacity-50"
            >
              {scrapeMut.isPending ? "Scaricamento in corso…" : "🌐 Importa ora"}
            </button>
          </div>
          {mismatchMsg && (
            <div className="text-xs bg-warning/10 border border-warning/40 text-warning rounded-lg p-2 space-y-2">
              <div>⚠ {mismatchMsg}</div>
              <button
                type="button"
                onClick={() => scrapeMut.mutate(true)}
                disabled={scrapeMut.isPending}
                className="btn-ghost text-xs text-warning border border-warning/40 rounded px-2 py-1"
              >
                Importa comunque
              </button>
            </div>
          )}
        </div>

        <div className="text-xs text-fg-muted text-center">
          — oppure incolla manualmente (Sky, Gazzetta, altri) —
        </div>
        <div>
          <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">Fonte</label>
          <select
            value={fonte}
            onChange={(e) => setFonte(e.target.value)}
            className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          >
            {FONTI.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">
            Testo paste
          </label>
          <textarea
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
            rows={14}
            placeholder={`Esempio:
JUVENTUS - Allegri
3-5-2
Probabili:
Szczesny 100%
Danilo 95%
Bremer (titolare)
Gatti (ballottaggio)
Cambiaso 70
Locatelli (in dubbio)
Rabiot
McKennie
...

Indisponibili:
Pogba (squalificato)
Milik (infortunato)
`}
            className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-accent"
          />
          <div className="text-[10px] text-fg-muted mt-1">
            Formati riconosciuti: "Cognome 85%", "Cognome 85", "Cognome (titolare/panchina/in
            dubbio/ballottaggio/indisponibile/squalificato/infortunato)", "Cognome" (default 100%).
            Intestazioni squadra/modulo/sezioni vengono ignorate.
          </div>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <button
            type="button"
            onClick={() => anteprimaMut.mutate()}
            disabled={
              !giornataId.trim() || !paste.trim() || anteprimaMut.isPending || importMut.isPending
            }
            className="btn-ghost text-sm border border-info/40 text-info hover:bg-info/10 disabled:opacity-50"
            title="Mostra il parsing senza scrivere nel DB"
          >
            {anteprimaMut.isPending ? "Anteprima…" : "👁 Anteprima"}
          </button>
          <button
            type="button"
            onClick={() => importMut.mutate()}
            disabled={!giornataId.trim() || !paste.trim() || importMut.isPending}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {importMut.isPending ? "Importazione…" : "📥 Importa probabili"}
          </button>
          {errore && <span className="text-xs text-danger">⚠ {errore}</span>}
        </div>
      </div>

      {anteprima && (
        <div className="card bg-info/5 border-info/40 space-y-2">
          <div className="font-bold text-info">
            👁 Anteprima parsing (nulla è stato scritto nel DB)
          </div>
          {/* Avviso intelligente se tutti i giocatori sono al 100% */}
          {anteprima.matched.length > 0 &&
            anteprima.matched.every((m) => m.probabilita === 100) && (
              <div className="text-xs text-warning bg-warning/10 border border-warning/30 rounded p-2">
                ⚠ Tutti i giocatori sono al <b>100%</b>: probabilmente il paste non contiene il
                segno <code>%</code> (Fantacalcio.it a volte renderizza le percentuali come grafica,
                non testo). Prova a copiare una zona diversa della pagina, o usa Sky/ Gazzetta che
                hanno % come testo.
              </div>
            )}
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <div className="text-xs text-fg-muted uppercase tracking-wider">Abbinati</div>
              <div className="font-mono font-bold text-success text-xl">
                {anteprima.matched.length}
              </div>
            </div>
            <div>
              <div className="text-xs text-fg-muted uppercase tracking-wider">Non abbinati</div>
              <div className="font-mono font-bold text-warning text-xl">
                {anteprima.non_abbinati.length}
              </div>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto bg-bg-card/40 rounded">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-primary-deep">
                <tr className="text-fg-muted uppercase tracking-wider">
                  <th className="text-left px-2 py-1">Input</th>
                  <th className="text-left px-2 py-1">Match DB</th>
                  <th className="text-right px-2 py-1">%</th>
                  <th className="text-left px-2 py-1">Note</th>
                </tr>
              </thead>
              <tbody>
                {anteprima.matched.map((m, i) => {
                  const pillCls = badgeProbabilita(m.probabilita).cls;
                  return (
                    <tr key={i} className="border-b border-primary-soft/30 last:border-b-0">
                      <td className="px-2 py-1 font-mono">{m.nome_input}</td>
                      <td className="px-2 py-1">{m.nome_db}</td>
                      <td className="px-2 py-1 text-right">
                        <span className={`font-mono font-bold px-1 rounded ${pillCls}`}>
                          {m.probabilita === 0 ? "OUT" : `${m.probabilita}%`}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-fg-muted italic">{m.note ?? ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {anteprima.non_abbinati.length > 0 && (
            <div className="text-xs">
              <div className="text-fg-muted uppercase tracking-wider mb-1">Non abbinati:</div>
              <ul className="font-mono text-warning max-h-32 overflow-y-auto space-y-0.5">
                {anteprima.non_abbinati.map((n, i) => (
                  <li key={i}>• {n}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {report && (
        <div className="card bg-success/10 border-success/40 space-y-2">
          <div className="font-bold text-success">✓ Import completato</div>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div>
              <div className="text-xs text-fg-muted uppercase tracking-wider">Parsate</div>
              <div className="font-mono font-bold text-accent text-xl">{report.parsate}</div>
            </div>
            <div>
              <div className="text-xs text-fg-muted uppercase tracking-wider">Abbinate</div>
              <div className="font-mono font-bold text-success text-xl">{report.abbinate}</div>
            </div>
            <div>
              <div className="text-xs text-fg-muted uppercase tracking-wider">Non abbinate</div>
              <div className="font-mono font-bold text-warning text-xl">
                {report.non_abbinate.length}
              </div>
            </div>
          </div>
          {report.non_abbinate.length > 0 && (
            <div>
              <div className="text-xs text-fg-muted uppercase tracking-wider mb-1">
                Nomi non abbinati (probabili refusi o giocatori fuori SA):
              </div>
              <ul className="text-xs text-warning font-mono space-y-0.5 max-h-40 overflow-y-auto">
                {report.non_abbinate.map((n, i) => (
                  <li key={i}>• {n}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </MobileShell>
  );
}
